import type {
  IAnalysisResult,
  IAnalysisTestSingleSelect,
  IAnalysisTestProveBased,
  IAnalysisTestValueBased,
  IAnalysisTestMultiSelectText,
  IAnalysisTestMultiSelectNumber,
  ITextList,
  IReportDescriptionItem,
} from '@team-milva/lab-models/resources'
import { RESULT_COLORS } from '@team-milva/lab-models/resources'
import type {
  ILabBase1ResultSample,
  TLabBase1ResultValue,
} from '@team-milva/lab-pdfs'
import type { Analysis } from '@/globals/typescript/models/Analysis'
import { t, tIn } from '@/locales'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type { Order } from '@/globals/typescript/models/Order'
import { getSampleLabel } from '@/globals/typescript/utils/sampleLabel'
import { withResultSuffix } from '@/globals/typescript/utils/resultSuffix'

// ---- Suffix label --------------------------------------------------------

// A multi-select row's optional display suffix (MIL-395) — an ordinary
// ReportDescriptionItem, resolved through the same locale-fallback chain as
// the subject label itself. Anything unresolvable yields '', which
// withResultSuffix() renders as the bare label.
function resolveSuffixText(
  suffixID: string | null | undefined,
  allReportDescriptionItems: IReportDescriptionItem[],
  locale: string
): string {
  if (!suffixID) return ''
  const item = allReportDescriptionItems.find((x) => x.id === suffixID)
  if (!item) return ''
  return (
    item.title[locale] ??
    Object.values(item.title).find((v) => v !== '') ??
    ''
  )
}

// ---- Unit label ----------------------------------------------------------

function getUnitLabel(
  unitID: string,
  systemLists: ITextList[],
  locale: string
): string {
  if (!unitID) return ''
  const list = systemLists.find((l) => l.systemKey === 'result-units')
  if (!list) return ''
  const item = list.items[unitID]
  return (
    item?.text[locale] ??
    Object.values(item?.text ?? {}).find((v) => v !== '') ??
    ''
  )
}

// Resolves one prove-based outcome to the string that prints on the report:
// the lab's configured label if it set one, otherwise Ja / Nej — both in the
// *report's* locale, never the reading user's.
function resolveProveBasedLabel(
  isProved: boolean,
  textID: string | null,
  systemLists: ITextList[],
  locale: string
): string {
  const defaultLabel = tIn(locale, isProved ? 'term.yes' : 'term.no')
  if (!textID) return defaultLabel
  const list = systemLists.find((l) => l.systemKey === 'result-texts')
  const item = list?.items[textID]
  if (!item) return defaultLabel
  return getTextObjValue(item.text, locale) || defaultLabel
}

// ---- Per-test result resolution ------------------------------------------

function resolveTestResult(
  result: IAnalysisResult | undefined,
  analysisTest: AnalysisTest | undefined,
  allReportDescriptionItems: IReportDescriptionItem[],
  systemLists: ITextList[],
  locale: string
): TLabBase1ResultValue {
  if (!result) return null

  const value = result.result

  if (value.type === 'prove-based') {
    if (value.isProved === null) return null
    const test =
      analysisTest?.type === 'prove-based'
        ? (analysisTest as IAnalysisTestProveBased)
        : null
    // The configured label *replaces* the outcome text, it is never appended:
    // unlike value-based there is no measured value to append it to.
    return {
      type: 'inline',
      value: resolveProveBasedLabel(
        value.isProved,
        value.isProved ? (test?.provedTextID ?? null) : (test?.notProvedTextID ?? null),
        systemLists,
        locale
      ),
    }
  }

  if (value.type === 'single-select') {
    if (!value.answerID) return null
    const test =
      analysisTest?.type === 'single-select'
        ? (analysisTest as IAnalysisTestSingleSelect)
        : null
    const answer = test?.answers.find((x) => x.id === value.answerID)
    const text =
      answer?.text[locale] ??
      Object.values(answer?.text ?? {}).find((v) => v !== '') ??
      ''
    return text ? { type: 'inline', value: text } : null
  }

  if (value.type === 'value-based') {
    const test =
      analysisTest?.type === 'value-based'
        ? (analysisTest as IAnalysisTestValueBased)
        : null
    const unit = test ? getUnitLabel(test.unitID, systemLists, locale) : ''

    if (value.specialValue === 'too-low') {
      const limit = test?.minLimit ?? null
      if (limit != null) {
        const formatted = new Intl.NumberFormat(locale).format(limit)
        return {
          type: 'inline',
          value: unit ? `< ${formatted} ${unit}` : `< ${formatted}`,
        }
      }
      return { type: 'inline', value: t('W: For lavt til at måle') }
    }

    if (value.specialValue === 'too-high') {
      const limit = test?.maxLimit ?? null
      if (limit != null) {
        const formatted = new Intl.NumberFormat(locale).format(limit)
        return {
          type: 'inline',
          value: unit ? `> ${formatted} ${unit}` : `> ${formatted}`,
        }
      }
      return { type: 'inline', value: t('W: For højt til at måle') }
    }

    if (value.value !== null) {
      const formatted = new Intl.NumberFormat(locale).format(value.value)
      return {
        type: 'inline',
        value: unit ? `${formatted} ${unit}` : formatted,
      }
    }

    return null
  }

  if (value.type === 'multi-select-text') {
    if (value.nothingToAdd || value.items.length === 0) {
      return { type: 'rows', rows: [] }
    }
    const test =
      analysisTest?.type === 'multi-select-text'
        ? (analysisTest as IAnalysisTestMultiSelectText)
        : null

    const rows = value.items.map((item) => {
      // A ReportDescriptionItem is its own document with a globally unique
      // id, so there is no "which list" indirection to resolve first.
      const reportDescriptionItem = allReportDescriptionItems.find(
        (x) => x.id === item.reportDescriptionItemID
      )
      const label =
        reportDescriptionItem?.title[locale] ??
        Object.values(reportDescriptionItem?.title ?? {}).find((v) => v !== '') ??
        item.reportDescriptionItemID
      const suffixedLabel = withResultSuffix(
        label,
        resolveSuffixText(item.suffixID, allReportDescriptionItems, locale)
      )
      const answer = test?.answers.find((x) => x.id === item.answerID)
      const answerValue =
        answer?.text[locale] ??
        Object.values(answer?.text ?? {}).find((v) => v !== '') ??
        ''
      return {
        label: suffixedLabel,
        value: answerValue,
        reportDescriptionItemID: item.reportDescriptionItemID,
      }
    })

    // An item here never has a value to give when the test's answers are
    // empty (answerMode 'none') — '–' would misleadingly read as missing,
    // so the report leaves the value blank instead. Applies regardless of
    // answerMode, matching what was asked for on the report side.
    return { type: 'rows', rows, hideEmptyDash: true }
  }

  if (value.type === 'multi-select-number') {
    if (value.nothingToAdd || value.items.length === 0) {
      return { type: 'rows', rows: [] }
    }
    const test =
      analysisTest?.type === 'multi-select-number'
        ? (analysisTest as IAnalysisTestMultiSelectNumber)
        : null
    const unit = test ? getUnitLabel(test.unitID, systemLists, locale) : ''

    const rows = value.items.map((item) => {
      // See the matching comment in the multi-select-text branch above.
      const reportDescriptionItem = allReportDescriptionItems.find(
        (x) => x.id === item.reportDescriptionItemID
      )
      const label =
        reportDescriptionItem?.title[locale] ??
        Object.values(reportDescriptionItem?.title ?? {}).find((v) => v !== '') ??
        item.reportDescriptionItemID
      const suffixedLabel = withResultSuffix(
        label,
        resolveSuffixText(item.suffixID, allReportDescriptionItems, locale)
      )

      const isOverride = item.answerOverrideID != null
      let rowValue = ''
      if (isOverride) {
        const override = test?.answerOverrides.find(
          (x) => x.id === item.answerOverrideID
        )
        rowValue =
          override?.text[locale] ??
          override?.text['en'] ??
          Object.values(override?.text ?? {}).find((v) => v) ??
          ''
      } else if (item.value !== null) {
        const formatted = new Intl.NumberFormat(locale).format(item.value)
        rowValue = unit ? `${formatted} ${unit}` : formatted
      }

      return {
        label: suffixedLabel,
        // The suffix is display-only, so ordering deliberately keys off the
        // bare subject name (MIL-370's rule, unchanged by MIL-395) — otherwise
        // "Stachybotrys spp" would sort under a different letter than
        // "Stachybotrys".
        sortLabel: label,
        value: rowValue,
        isOverride,
        rawValue: item.value,
        reportDescriptionItemID: item.reportDescriptionItemID,
      }
    })

    // Overrides sort to the top by name; numeric-valued rows follow, descending
    // by their raw value, so the highest count reads first (MIL-370). A row with
    // neither (shouldn't normally occur) sorts to the end of the numeric group
    // rather than being dropped.
    const sortedRows: {
      label: string
      value: string
      reportDescriptionItemID?: string
    }[] = [
      ...rows
        .filter((row) => row.isOverride)
        .sort((a, b) => a.sortLabel.localeCompare(b.sortLabel)),
      ...rows
        .filter((row) => !row.isOverride)
        .sort((a, b) => (b.rawValue ?? -Infinity) - (a.rawValue ?? -Infinity)),
    ].map(({ label, value: rowValue, reportDescriptionItemID }) => ({
      label,
      value: rowValue,
      reportDescriptionItemID,
    }))

    // Only when the test has the sum option on: the Sum field does not exist in result
    // entry otherwise, so a saved sum is a leftover and must not reach the report.
    if (value.sumValue && test?.options.hasSumValue) {
      sortedRows.push({ label: t('W: Sum'), value: value.sumValue })
    }

    return { type: 'rows', rows: sortedRows }
  }

  return null
}

// ---- Per-test label resolution -------------------------------------------

function resolveTestLabel(
  result: IAnalysisResult | undefined,
  analysisTest: AnalysisTest | undefined,
  systemLists: ITextList[],
  locale: string
): string | null {
  if (!result || !analysisTest) return null
  if (result.result.type !== 'value-based' || analysisTest.type !== 'value-based') return null

  const value = result.result
  const test = analysisTest as IAnalysisTestValueBased
  const config = test.colorConfig
  if (!config?.enabled || config.conditions.length === 0) return null

  let numValue: number
  if (value.value !== null) {
    numValue = value.value
  } else if (value.specialValue === 'too-low' && test.minLimit != null) {
    numValue = test.minLimit
  } else if (value.specialValue === 'too-high' && test.maxLimit != null) {
    numValue = test.maxLimit
  } else {
    return null
  }

  const match = config.conditions.find((c) => {
    switch (c.operator) {
      case '<': return numValue < c.value
      case '<=': return numValue <= c.value
      case '>=': return numValue >= c.value
      case '>': return numValue > c.value
      default: return false
    }
  })

  if (!match?.textID) return null

  const list = systemLists.find((l) => l.systemKey === 'result-texts')
  if (!list) return null
  const item = list.items[match.textID]
  return item?.text[locale] ?? Object.values(item?.text ?? {}).find((v) => v !== '') ?? null
}

// ---- Per-test color resolution -------------------------------------------

function resolveTestColor(
  result: IAnalysisResult | undefined,
  analysisTest: AnalysisTest | undefined
): { background: string; text: string } | null {
  if (!result || !analysisTest) return null

  const value = result.result

  if (value.type === 'prove-based' && analysisTest.type === 'prove-based') {
    const config = (analysisTest as IAnalysisTestProveBased).colorConfig
    if (!config?.enabled || value.isProved === null) return null
    const colorID = value.isProved ? config.provedColorID : config.notProvedColorID
    if (!colorID) return null
    const color = Object.values(RESULT_COLORS).find((x) => x.id === colorID)
    if (!color) return null
    return { background: color.color, text: color.textColor }
  }

  if (value.type === 'single-select' && analysisTest.type === 'single-select') {
    // Mirrors the lab-cloud resolver (MIL-550): read live from the test, like the answer's text.
    const config = (analysisTest as IAnalysisTestSingleSelect).colorConfig
    if (!config?.enabled || !value.answerID) return null
    const colorID = config.answerColorIDs[value.answerID]
    if (!colorID) return null
    const color = Object.values(RESULT_COLORS).find((x) => x.id === colorID)
    if (!color) return null
    return { background: color.color, text: color.textColor }
  }

  if (value.type === 'value-based' && analysisTest.type === 'value-based') {
    const test = analysisTest as IAnalysisTestValueBased
    const config = test.colorConfig
    if (!config?.enabled || config.conditions.length === 0) return null
    let numValue: number
    if (value.value !== null) {
      numValue = value.value
    } else if (value.specialValue === 'too-low' && test.minLimit != null) {
      numValue = test.minLimit
    } else if (value.specialValue === 'too-high' && test.maxLimit != null) {
      numValue = test.maxLimit
    } else {
      return null
    }
    const match = config.conditions.find((c) => {
      switch (c.operator) {
        case '<': return numValue < c.value
        case '<=': return numValue <= c.value
        case '>=': return numValue >= c.value
        case '>': return numValue > c.value
        default: return false
      }
    })
    if (!match?.colorID) return null
    const color = Object.values(RESULT_COLORS).find((x) => x.id === match.colorID)
    if (!color) return null
    return { background: color.color, text: color.textColor }
  }

  return null
}

// ---- Main resolver -------------------------------------------------------

/**
 * Resolves all raw analysis results for an order into the pre-rendered
 * ILabBase1ResultSample[] shape expected by renderLabBase1.
 *
 * Only analyses assigned to the given reportTemplateID are included.
 * Cancelled analyses are skipped.
 */
export function resolveOrderResultsForReport(params: {
  order: Order
  reportTemplateID: string
  tatGroupID?: string
  results: IAnalysisResult[]
  allAnalyses: Analysis[]
  allAnalysisTests: AnalysisTest[]
  allReportDescriptionItems: IReportDescriptionItem[]
  systemLists: ITextList[]
  locale: string
}): ILabBase1ResultSample[] {
  const {
    order,
    reportTemplateID,
    tatGroupID,
    results,
    allAnalyses,
    allAnalysisTests,
    allReportDescriptionItems,
    systemLists,
    locale,
  } = params

  const relevantAnalysisIDs = new Set(
    Object.entries(order.reportTemplateAssignments)
      .filter(
        ([key, tid]) =>
          tid === reportTemplateID &&
          (!tatGroupID || key.startsWith(`${tatGroupID}__`))
      )
      .map(([key]) => (tatGroupID ? key.split('__')[1] : key))
  )

  const resolvedSamples: ILabBase1ResultSample[] = []

  for (const sample of order.samples) {
    if (sample.status === 'cancelled') continue
    // A delivery's report only holds the samples of that delivery. Without
    // this, samples in another TAT group that share the analysis and the
    // report template land in every delivery's report.
    if (tatGroupID && sample.tatGroupID !== tatGroupID) continue

    const resolvedAnalyses = []

    for (const sampleAnalysis of sample.analyses) {
      if (!relevantAnalysisIDs.has(sampleAnalysis.analysisID)) continue
      if (sampleAnalysis.status === 'cancelled') continue

      const analysis = allAnalyses.find(
        (x) => x.id === sampleAnalysis.analysisID
      )
      const analysisName = analysis
        ? analysis.getName(locale)
        : sampleAnalysis.analysisID

      // Filtered on the flag, never on `status`: a turned-off test is marked
      // `completed` like any other once its analysis completes, so a
      // status-based check would let it through and print it with a dash.
      // Kept identical to the same filter in the other
      // resolveOrderResultsForReport — the preview and the generated PDF have
      // to agree.
      const reportableTests = sampleAnalysis.analysisTests.filter(
        (sampleTest) => !(sampleTest.options?.isExcludedFromReport ?? false)
      )

      const resolvedTests = reportableTests.map((sampleTest) => {
        const analysisTest = allAnalysisTests.find(
          (x) => x.id === sampleTest.analysisTestID
        )
        const testName = analysisTest
          ? analysisTest.getDisplayName(locale)
          : sampleTest.analysisTestID

        const resultDoc = results.find(
          (r) =>
            r.sampleID === sample.sampleID &&
            r.analysisTestID === sampleTest.analysisTestID &&
            r.analysisID === sampleAnalysis.analysisID
        )

        const result = resolveTestResult(
          resultDoc,
          analysisTest,
          allReportDescriptionItems,
          systemLists,
          locale
        )
        const label = resolveTestLabel(resultDoc, analysisTest, systemLists, locale)
        const resultWithLabel =
          label && result?.type === 'inline'
            ? { type: 'inline' as const, value: `${result.value} — ${label}` }
            : result

        return {
          name: testName,
          result: resultWithLabel,
          resultColor: resolveTestColor(resultDoc, analysisTest),
        }
      })

      if (resolvedTests.length > 0) {
        resolvedAnalyses.push({
          name: analysisName,
          analysisID: sampleAnalysis.analysisID,
          tests: resolvedTests,
          note: sampleAnalysis.comment || undefined,
        })
      }
    }

    if (resolvedAnalyses.length > 0) {
      resolvedSamples.push({
        sampleID: sample.sampleID,
        label: getSampleLabel(sample.sampleNumber, locale),
        sampleNumber: sample.sampleNumber,
        analyses: resolvedAnalyses,
        builtYear: sample.builtYear !== undefined ? String(sample.builtYear) : undefined,
        buildingLocation: sample.buildingLocation || undefined,
        material: sample.material || undefined,
        note: sample.additionalNotes || undefined,
      })
    }
  }

  return resolvedSamples
}

